import React, { memo } from "react"

/**
 * Props for the TabButton component.
 */
interface TabButtonProps {
  /**
   * The label text displayed on the button.
   */
  label: string

  /**
   * Boolean flag indicating if the tab is active.
   */
  isActive: boolean

  /**
   * Function to execute when the button is clicked.
   */
  onClick: () => void

  /**
   * Optional unique identifier for the button element.
   */
  id: string
}

/**
 * TabButton component renders a button for tab navigation.
 * It highlights when active and executes an onClick function when clicked.
 *
 * @param {TabButtonProps} props - The properties for the TabButton component.
 * @returns {JSX.Element} The rendered tab button.
 */
const TabButton: React.FC<TabButtonProps> = memo(
  ({ label, isActive, onClick, id }) => (
    <button
      data-automation-id={id}
      id={id}
      onClick={onClick}
      className={`pb-2 px-4 text-sm font-bold flex-1 text-center ${
        isActive
          ? "text-red-600 border-b-2 border-red-600"
          : "text-gray-500 hover:text-gray-700"
      }`}
      role="tab"
      aria-selected={isActive}
    >
      {label}
    </button>
  )
)

export default TabButton
